// Where each Gross Margin line comes from (documentation/business-rules/finance-calculations.md,
// section 2; built by lambda GrossMarginReport). Keyed by the report field without Actual/Budget.

export interface GrossMarginSource {
    // Where the stored amount comes from
    source: string
    // Formula for derived lines
    formula?: string
}

export const GROSS_MARGIN_SOURCES: Record<string, GrossMarginSource> = {
    consultingIncome: { source: "Acumatica GL transactions and budgets for the Consulting Income account class (CONSULT)." },
    disbursementIncome: { source: "Acumatica GL transactions and budgets for the Disbursement Income account class (DISBURS)." },
    accruedRevenue: { source: "Acumatica GL transactions on account 410100 (Accrued Revenue)." },
    accruedDisbursement: { source: "Acumatica GL transactions on account 410200 (Accrued Disbursement Revenue)." },
    totalSalesRevenue: {
        source: "Calculated by the Gross Margin sync.",
        formula: "Actual: Consulting Income + Disbursement Income + Accrued Revenue + Accrued Disbursement Revenue. Budget: Consulting Income + Disbursement Income budgets.",
    },
    directCost: { source: "Actual: GL transactions for the Direct Cost account class (DIRECTCOST). Budget: account 510400." },
    accruedCost: { source: "Acumatica GL transactions on account 552010 (Accrued Cost)." },
    totalDisbursementCost: {
        source: "Calculated by the Gross Margin sync.",
        formula: "Actual: Direct Costs + Accrued Cost. Budget: account 510400.",
    },
    netRevenue: { source: "Calculated by the Gross Margin sync.", formula: "Total Sales Revenue − Total Disbursement Cost." },
    hoSupportRevenue: { source: "Acumatica GL transactions for the HO Support Revenue account class (INTERCOREV)." },
    rentalIncome: { source: "Acumatica GL transactions for the Rental Income account class (BUDGETMAN)." },
    totalRevenue: {
        source: "Calculated by the Gross Margin sync.",
        formula: "Actual: Total Sales Revenue + Other Revenue (returns, HO support, rental). Budget: Total Sales Revenue budget.",
    },
    directHourlyCost: { source: "Acumatica GL transactions and budgets on account 510000 (Direct Hourly Cost)." },
    subConsultant: { source: "Acumatica GL transactions and budgets on account 510100 (Sub-consultant)." },
    equipmentHour: { source: "Acumatica GL transactions and budgets on account 510150 (Equipment Hour)." },
    directPayrollCost: { source: "Acumatica GL transactions for the payroll cost class (COGS PAYROLL)." },
    accruedDirectCost: { source: "Acumatica GL transactions on account 552000 (Accrued Direct Cost)." },
    totalCostOfRevenue: {
        source: "Calculated by the Gross Margin sync.",
        formula: "Actual: 510000 + 510100 + 510150 + payroll + 552000. Budget: 510000 + 510100 + 510150.",
    },
    grossMargin: { source: "Calculated by the Gross Margin sync.", formula: "Net Revenue − Total Cost of Revenue." },
    gmNetRevenue: { source: "Calculated by the Gross Margin sync.", formula: "Same as Gross Margin (actual); compared with the Gross Margin budget." },
    gmPct: { source: "Calculated on this page.", formula: "Gross Margin ÷ Total Revenue." },
    gmNetPct: { source: "Calculated on this page.", formula: "GM (Net Revenue) ÷ Net Revenue." },
    consultingRevenue: { source: "Calculated on this page.", formula: "Actual: Consulting Income + Accrued Revenue. Budget: Consulting Income budget." },
    consultingCogs: { source: "Calculated on this page.", formula: "Actual: Direct Hourly Cost + Sub-consultant. Budget: Direct Hourly Cost + Sub-consultant budgets." },
    consultingRevenueGM: { source: "Calculated on this page.", formula: "Consulting Revenue − Consulting COGS." },
    consultingRevenueGMPct: { source: "Calculated on this page.", formula: "Consulting Revenue GM ÷ Consulting Revenue." },
}

// Lines calculated on the page (not stored): the modal shows the formula only
export const PAGE_CALCULATED_FIELDS = new Set(["gmPct", "gmNetPct", "consultingRevenue", "consultingCogs", "consultingRevenueGM", "consultingRevenueGMPct"])

export const SECTION_INFO: Record<string, string> = {
    revenue: "Revenue booked in Acumatica for the selected periods, branches and departments: sales revenue, disbursement cost and other revenue.",
    costOfRevenue: "Direct costs of delivering the work (hourly cost, sub-consultants, equipment, payroll and accruals).",
    grossMargin: "What is left of revenue after the cost of revenue, in dollars and as a percentage.",
    consultingBreakdown: "Gross margin of consulting work only (consulting revenue minus consulting cost).",
}
